import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Settings, ShieldCheck, AlertTriangle, RefreshCw, Coins, Clock, Play, Mail, Trash2 } from "lucide-react";
import api, { apiError } from "@/lib/api";

const Field = ({ label, k, placeholder, type = "text", hint, form, set }) => (
  <div>
    <label className="text-sm text-slate-400">{label}</label>
    <input data-testid={`settings-${k}`} type={type} value={form[k]} onChange={set(k)} placeholder={placeholder}
      className="w-full mt-1.5 bg-[#0D0E12] border border-amber-500/20 focus:border-amber-500 rounded-xl px-4 py-3 outline-none font-mono-fin text-sm" />
    {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
  </div>
);

const Toggle = ({ label, k, hint, form, toggle }) => (
  <button type="button" data-testid={`toggle-${k}`} onClick={() => toggle(k)}
    className="flex items-center justify-between w-full rounded-xl bg-black/30 border border-amber-500/15 p-4">
    <div className="text-left"><div className="text-sm font-medium">{label}</div><div className="text-xs text-slate-500">{hint}</div></div>
    <div className={`w-12 h-6 rounded-full p-0.5 transition-all ${form[k] ? "bg-emerald-500" : "bg-slate-700"}`}>
      <div className={`w-5 h-5 rounded-full bg-white transition-all ${form[k] ? "translate-x-6" : ""}`} />
    </div>
  </button>
);

const Status = ({ label, ok }) => (
  <span>{label}: <b className={ok ? "text-emerald-400" : "text-red-400"}>{ok ? "SET" : "NOT SET"}</b></span>
);

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [form, setForm] = useState({
    bscscan_api_key: "", fee_wallet: "", fee_wallet_private_key: "",
    collection_wallet: "", withdrawal_wallet: "", withdrawal_private_key: "",
    confirmations_required: 12, auto_detect_enabled: false, payout_enabled: false,
    p2p_min_transfer: 0, p2p_daily_cap: 0,
  });
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [pkgRoi, setPkgRoi] = useState([]);
  const [sched, setSched] = useState({ run_time: "00:00" });
  const [schedInfo, setSchedInfo] = useState(null);
  const [savingSched, setSavingSched] = useState(false);
  const [running, setRunning] = useState(false);
  const [resetPhrase, setResetPhrase] = useState("");
  const [resetting, setResetting] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const resetData = async () => {
    if (resetPhrase !== "DELETE ALL DATA") { toast.error('Type "DELETE ALL DATA" exactly'); return; }
    if (!window.confirm("This permanently deletes ALL members and transaction data. Admin account and wallet settings are kept. Continue?")) return;
    setResetting(true); setResetResult(null);
    try {
      const { data } = await api.post("/admin/reset-data", { confirm: resetPhrase });
      setResetResult(data.deleted);
      setResetPhrase("");
      toast.success("Test data wiped. Fresh start ready.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setResetting(false);
  };
  const loadSched = async () => {
    try { const { data } = await api.get("/admin/income-schedule"); setSched({ run_time: data.run_time }); setSchedInfo(data); } catch (e) { /* ignore */ }
  };
  useEffect(() => { loadSched(); }, []);
  const saveSched = async () => {
    setSavingSched(true);
    try { await api.post("/admin/income-schedule", { run_time: sched.run_time }); toast.success("Daily income run time saved"); await loadSched(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setSavingSched(false);
  };
  const runNow = async () => {
    setRunning(true);
    try { const { data } = await api.post("/admin/run-income-cycle"); toast.success(`Income cycle run for ${data.processed_users} members`); await loadSched(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setRunning(false);
  };
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const sendTestEmail = async () => {
    setTesting(true); setTestResult(null);
    try {
      const { data } = await api.post("/admin/test-email", testTo ? { to: testTo } : {});
      setTestResult(data);
      if (data.ok) toast.success(`Test email sent via ${data.provider} to ${data.to}`);
      else toast.error(`Email failed (${data.provider}): ${data.error || data.status}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setTesting(false);
  };
  useEffect(() => { api.get("/admin/package-roi").then(({ data }) => setPkgRoi(data)).catch(() => {}); }, []);
  const setPkgRate = async (id, rate) => {
    try {
      await api.post("/admin/package-roi", { package_id: id, rate: rate === "" || rate === null ? null : Number(rate) });
      toast.success(rate === "" || rate === null ? "Daily rate cleared — uses default range" : "Daily ROI set for package");
      const { data } = await api.get("/admin/package-roi"); setPkgRoi(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const load = async () => {
    const { data } = await api.get("/admin/settings");
    setS(data);
    setForm((f) => ({
      ...f,
      fee_wallet: data.fee_wallet || "",
      collection_wallet: data.collection_wallet || "",
      withdrawal_wallet: data.withdrawal_wallet || "",
      confirmations_required: data.confirmations_required || 12,
      auto_detect_enabled: data.auto_detect_enabled || false,
      payout_enabled: data.payout_enabled || false,
      p2p_min_transfer: data.p2p_min_transfer || 0,
      p2p_daily_cap: data.p2p_daily_cap || 0,
    }));
  };
  useEffect(() => { load().catch(() => toast.error("Load failed")); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (k) => setForm((f) => ({ ...f, [k]: !f[k] }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, confirmations_required: Number(form.confirmations_required), p2p_min_transfer: Number(form.p2p_min_transfer) || 0, p2p_daily_cap: Number(form.p2p_daily_cap) || 0 };
      ["bscscan_api_key", "fee_wallet_private_key", "withdrawal_private_key"].forEach((k) => { if (!payload[k]) delete payload[k]; });
      await api.put("/admin/settings", payload);
      toast.success("Crypto settings saved");
      setForm({ ...form, bscscan_api_key: "", fee_wallet_private_key: "", withdrawal_private_key: "" });
      await load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    setBusy(false);
  };

  const scanNow = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/admin/scan-deposits");
      toast.success(data.enabled ? `Scanned ${data.scanned} wallets · ${data.credited} new deposit(s)` : "Auto-detect is disabled — enable 'Auto-detect & sweep deposits' and save to activate scanning");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    setScanning(false);
  };

  if (!s) return <div className="text-amber-400 font-mono-fin">Loading...</div>;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-settings-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-2xl font-bold flex items-center gap-2"><Settings className="w-6 h-6 text-amber-400" /> Crypto Settings (USDT BEP20)</h1>
        <button data-testid="scan-now-btn" onClick={scanNow} disabled={scanning} className="gold-btn px-5 py-2.5 rounded-xl font-bold text-sm flex items-center gap-2 disabled:opacity-60">
          <RefreshCw className={`w-4 h-4 ${scanning ? "animate-spin" : ""}`} /> {scanning ? "Scanning..." : "Scan Deposits Now"}
        </button>
      </div>

      <div className="rounded-2xl bg-amber-500/10 border border-amber-500/20 p-4 flex gap-3 text-sm">
        <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0" />
        <div className="text-amber-200">
          Each user gets a unique BEP20 deposit address. When USDT arrives it is auto-credited (every ~30s) and swept to your Collection wallet (gas paid by the Fee wallet). Withdrawals are sent from the Withdrawal wallet. Secrets are encrypted and never shown again — leave key fields blank to keep existing values.
          <div className="mt-2 flex flex-wrap gap-4 text-xs">
            <Status label="Etherscan Key" ok={s.bscscan_api_key_set} />
            <Status label="Fee Key" ok={s.fee_key_set} />
            <Status label="Withdrawal Key" ok={s.withdrawal_key_set} />
          </div>
        </div>
      </div>

      <form onSubmit={save} className="glass rounded-2xl p-6 space-y-5">
        <Field label="Etherscan V2 API Key (BSC chainId 56)" k="bscscan_api_key" placeholder="Leave blank to keep existing" hint="Used to auto-detect on-chain deposits. Etherscan V2 works for BSC." form={form} set={set} />

        <div className="border-t border-amber-500/15 pt-5 space-y-5">
          <div className="flex items-center gap-2 text-amber-400"><ShieldCheck className="w-4 h-4" /><span className="text-sm font-semibold">Fee / Gas Wallet (pays BNB for sweeps)</span></div>
          <Field label="Fee Wallet Address" k="fee_wallet" placeholder="0x... (must hold BNB for gas)" form={form} set={set} />
          <Field label="Fee Wallet Private Key" k="fee_wallet_private_key" type="password" placeholder="Leave blank to keep existing" hint="Encrypted at rest." form={form} set={set} />
        </div>

        <div className="border-t border-amber-500/15 pt-5 space-y-5">
          <div className="flex items-center gap-2 text-amber-400"><ShieldCheck className="w-4 h-4" /><span className="text-sm font-semibold">Collection Wallet (receives swept deposits)</span></div>
          <Field label="Collection Wallet Address" k="collection_wallet" placeholder="0x... destination for user deposits" form={form} set={set} />
        </div>

        <div className="border-t border-amber-500/15 pt-5 space-y-5">
          <div className="flex items-center gap-2 text-red-400"><ShieldCheck className="w-4 h-4" /><span className="text-sm font-semibold">Withdrawal Wallet (auto-sends payouts)</span></div>
          <Field label="Withdrawal Wallet Address" k="withdrawal_wallet" placeholder="0x... hot wallet for payouts" form={form} set={set} />
          <Field label="Withdrawal Wallet Private Key" k="withdrawal_private_key" type="password" placeholder="Leave blank to keep existing" hint="Encrypted at rest. Used to send USDT automatically." form={form} set={set} />
        </div>

        <Field label="Confirmations Required" k="confirmations_required" type="number" placeholder="12" form={form} set={set} />

        <div className="border-t border-amber-500/15 pt-5 space-y-5">
          <div className="flex items-center gap-2 text-amber-400"><ShieldCheck className="w-4 h-4" /><span className="text-sm font-semibold">P2P Transfer Limits (member-to-downline)</span></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Minimum Transfer (USDT)" k="p2p_min_transfer" type="number" placeholder="0 = no minimum" hint="Smallest allowed single transfer. 0 disables the minimum." form={form} set={set} />
            <Field label="Daily Transfer Cap per User (USDT)" k="p2p_daily_cap" type="number" placeholder="0 = unlimited" hint="Max total a member can send per day. 0 = unlimited." form={form} set={set} />
          </div>
        </div>

        <Toggle label="Auto-detect & sweep deposits" k="auto_detect_enabled" hint="Scan user addresses every ~30s, credit & sweep to collection" form={form} toggle={toggle} />
        <Toggle label="Enable automated withdrawals" k="payout_enabled" hint="Send USDT programmatically from the withdrawal wallet when paying" form={form} toggle={toggle} />
        <button data-testid="save-settings-btn" disabled={busy} className="gold-btn w-full py-3 rounded-xl font-bold disabled:opacity-60">
          {busy ? "Saving..." : "Save Settings"}
        </button>
      </form>

      <div className="glass rounded-2xl p-6" data-testid="admin-package-roi">
        <h2 className="font-display text-xl font-bold flex items-center gap-2"><Coins className="w-5 h-5 text-amber-400" /> Daily Package ROI</h2>
        <p className="text-sm text-slate-400 mt-1 mb-4">Set the exact daily ROI % credited at the next <b>00:00 UTC</b> run to <b>all users</b> of a package. Set it before midnight. Leave blank to use the package's default range.</p>
        <div className="space-y-3">
          {pkgRoi.map((p) => (
            <div key={p.id} className="flex items-center gap-3 flex-wrap bg-[#0D0E12] border border-amber-500/15 rounded-xl px-4 py-3">
              <div className="flex-1 min-w-[9rem]">
                <div className="font-semibold text-sm">{p.name}</div>
                <div className="text-xs text-slate-500">${p.amount} · default {p.roi_min}%–{p.roi_max}%{p.daily_rate != null && <span className="text-emerald-400"> · today {p.daily_rate}%</span>}</div>
              </div>
              <input data-testid={`pkg-roi-input-${p.id}`} type="number" step="0.01" value={p.daily_rate ?? ""} placeholder="range"
                onChange={(e) => setPkgRoi((arr) => arr.map((x) => x.id === p.id ? { ...x, daily_rate: e.target.value } : x))}
                className="w-24 bg-black/40 border border-amber-500/20 rounded-lg px-3 py-2 text-sm outline-none font-mono-fin" />
              <span className="text-xs text-slate-500">%</span>
              <button data-testid={`pkg-roi-set-${p.id}`} onClick={() => setPkgRate(p.id, p.daily_rate)} className="gold-btn px-4 py-2 rounded-lg text-xs font-bold">Set</button>
              <button data-testid={`pkg-roi-clear-${p.id}`} onClick={() => setPkgRate(p.id, "")} className="px-3 py-2 rounded-lg text-xs border border-amber-500/30 text-amber-300 hover:bg-amber-500/10">Clear</button>
            </div>
          ))}
        </div>
      </div>

      <div className="glass rounded-2xl p-6" data-testid="admin-income-schedule">
        <h2 className="font-display text-xl font-bold flex items-center gap-2"><Clock className="w-5 h-5 text-amber-400" /> Daily Income Run Time</h2>
        <p className="text-sm text-slate-400 mt-1 mb-4">Set the <b>UTC</b> time when daily ROI + all MLM incomes are credited to every member. Runs automatically each weekday (Mon–Fri) at this time.</p>
        <div className="flex items-center gap-3 flex-wrap">
          <input data-testid="income-run-time" type="time" value={sched.run_time}
            onChange={(e) => setSched({ run_time: e.target.value })}
            className="bg-[#0D0E12] border border-amber-500/20 focus:border-amber-500 rounded-xl px-4 py-3 outline-none font-mono-fin text-sm" />
          <span className="text-xs text-slate-500">UTC</span>
          <button data-testid="save-income-time-btn" onClick={saveSched} disabled={savingSched} className="gold-btn px-5 py-3 rounded-xl font-bold text-sm disabled:opacity-60">{savingSched ? "Saving..." : "Save Time"}</button>
          <button data-testid="run-income-now-btn" onClick={runNow} disabled={running} className="px-5 py-3 rounded-xl font-bold text-sm border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 flex items-center gap-2 disabled:opacity-60"><Play className="w-4 h-4" /> {running ? "Running..." : "Run Now"}</button>
        </div>
        {schedInfo?.last_run_at && (
          <p className="text-xs text-emerald-400 mt-3" data-testid="income-last-run">Last run: {new Date(schedInfo.last_run_at).toLocaleString()} · {schedInfo.last_run_users ?? 0} members credited</p>
        )}
      </div>

      <div className="glass rounded-2xl p-6" data-testid="admin-email-test">
        <h2 className="font-display text-xl font-bold flex items-center gap-2"><Mail className="w-5 h-5 text-amber-400" /> Email Delivery Test</h2>
        <p className="text-sm text-slate-400 mt-1 mb-4">Send a test email to verify delivery. The result (provider, status &amp; any error) shows below — no need to dig through logs.</p>
        <div className="flex items-center gap-3 flex-wrap">
          <input data-testid="test-email-input" type="email" value={testTo}
            onChange={(e) => setTestTo(e.target.value)} placeholder="recipient@example.com (blank = your admin email)"
            className="flex-1 min-w-[240px] bg-[#0D0E12] border border-amber-500/20 focus:border-amber-500 rounded-xl px-4 py-3 outline-none text-sm" />
          <button data-testid="send-test-email-btn" onClick={sendTestEmail} disabled={testing} className="gold-btn px-5 py-3 rounded-xl font-bold text-sm disabled:opacity-60">{testing ? "Sending..." : "Send Test Email"}</button>
        </div>
        {testResult && (
          <div className={`mt-3 text-xs font-mono-fin rounded-lg p-3 border ${testResult.ok ? "bg-emerald-500/10 border-emerald-500/25 text-emerald-300" : "bg-red-500/10 border-red-500/25 text-red-300"}`} data-testid="test-email-result">
            <div>Provider: <b>{testResult.provider}</b> · To: {testResult.to} · {testResult.ok ? "SENT ✓" : "FAILED ✗"}</div>
            {testResult.status != null && <div>HTTP {testResult.status}</div>}
            {testResult.response && <div className="break-all mt-1 text-slate-400">{testResult.response}</div>}
            {testResult.error && <div className="break-all mt-1 text-slate-400">{testResult.error}</div>}
          </div>
        )}
      </div>

      <div className="rounded-2xl p-6 bg-red-500/5 border border-red-500/30" data-testid="danger-zone">
        <h2 className="font-display text-xl font-bold flex items-center gap-2 text-red-400"><Trash2 className="w-5 h-5" /> Danger Zone — Reset Test Data</h2>
        <p className="text-sm text-slate-400 mt-1 mb-2">Permanently deletes ALL members and every transaction record (deposits, withdrawals, incomes, investments, trades, transfers, adjustments) for a clean production launch.</p>
        <div className="text-xs text-emerald-300/90 bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-3 mb-4">
          <b>Kept safe:</b> your admin login and all configuration — crypto wallets &amp; private keys, package ROI rates, income schedule and the news popup. This cannot be undone.
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <input data-testid="reset-phrase-input" value={resetPhrase} onChange={(e) => setResetPhrase(e.target.value)}
            placeholder='Type: DELETE ALL DATA'
            className="flex-1 min-w-[240px] bg-[#0D0E12] border border-red-500/30 focus:border-red-500 rounded-xl px-4 py-3 outline-none text-sm font-mono-fin" />
          <button data-testid="reset-data-btn" onClick={resetData} disabled={resetting || resetPhrase !== "DELETE ALL DATA"}
            className="px-5 py-3 rounded-xl font-bold text-sm bg-red-600 hover:bg-red-500 text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
            {resetting ? "Wiping..." : "Delete All Test Data"}
          </button>
        </div>
        {resetResult && (
          <div className="mt-3 text-xs font-mono-fin rounded-lg p-3 border bg-emerald-500/10 border-emerald-500/25 text-emerald-300" data-testid="reset-result">
            Done. Removed — members: {resetResult.members}, deposits: {resetResult.deposits}, withdrawals: {resetResult.withdrawals}, incomes: {resetResult.incomes}, investments: {resetResult.investments}, trades: {resetResult.demo_trades}, transfers: {resetResult.transfers}, adjustments: {resetResult.admin_adjustments}
          </div>
        )}
      </div>
    </div>
  );
}
